import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { validationApi } from '../services/validationApi';
import { scraperCaseStatsApi, type ScraperCaseStats } from '../services/scraperApi';
import API from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import {
  CheckCircleIcon,
  ArrowPathIcon,
  EyeIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

type ReviewTab = 'ALL' | 'FOR_VALIDATION' | 'FOR_APPROVAL' | 'REJECTED';

interface ReviewCase {
  id: string;
  caseNumber: string;
  description: string;
  country: string;
  violationTypes: string[];
  status: string;
  validationStatus?: string;
  dataSource: string;
  sourceInformation?: string;
  createdAt: string;
  validationResults?: Array<{ confidence: number; completeness?: number | null }>;
}

// Documentation-coverage band from a 0–1 completeness score (mirrors the case-detail panel).
function coverageBand(pct: number): { label: string; color: string } {
  if (pct >= 90) return { label: 'Extensive', color: 'bg-green-100 text-green-700' };
  if (pct >= 70) return { label: 'Good', color: 'bg-green-100 text-green-700' };
  if (pct >= 40) return { label: 'Partial', color: 'bg-amber-100 text-amber-800' };
  return { label: 'Limited', color: 'bg-red-100 text-red-700' };
}

interface CasesResponse {
  cases: ReviewCase[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const subTabConfig: { key: ReviewTab; label: string; description: string }[] = [
  { key: 'ALL', label: 'All', description: 'All auto-sourced cases regardless of status' },
  { key: 'FOR_VALIDATION', label: 'Needs Review', description: 'AI could not decide — requires human review' },
  { key: 'FOR_APPROVAL', label: 'Validated', description: 'AI validated — awaiting final admin approval' },
  { key: 'REJECTED', label: 'Rejected', description: 'AI rejected — review to confirm or override' },
];

export default function ReviewQueueTab() {
  const [reviewTab, setReviewTab] = useState<ReviewTab>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // Case stats for tab counts
  const { data: caseStats } = useQuery<ScraperCaseStats>({
    queryKey: ['scraper-case-stats'],
    queryFn: () => scraperCaseStatsApi.get(),
  });

  // Review queue cases
  const { data: casesData, isLoading: casesLoading } = useQuery<CasesResponse>({
    queryKey: ['validation-cases', reviewTab, currentPage],
    queryFn: async () => {
      const params = new URLSearchParams({
        isAutoSourced: 'true',
        page: currentPage.toString(),
        limit: '20',
        ...(reviewTab !== 'ALL' ? { status: reviewTab } : { excludeStatus: 'ARCHIVED' }),
      });
      const response = await API.get(`/api/cases?${params}`);
      return response.data;
    },
  });

  // Batch validate mutation
  const batchValidateMutation = useMutation({
    mutationFn: (caseIds: string[]) => validationApi.validateBatch(caseIds),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['validation-cases'] });
      queryClient.invalidateQueries({ queryKey: ['validation-stats'] });
      queryClient.invalidateQueries({ queryKey: ['scraper-case-stats'] });
      showToast(
        'success',
        `Batch complete: ${result.processed} processed, ${result.failed} failed`
      );
    },
    onError: () => showToast('error', 'Batch validation failed'),
  });

  const cases = casesData?.cases || [];
  const pagination = casesData?.pagination;

  const confidenceBar = (confidence: number, size: 'sm' | 'lg' = 'sm') => {
    const pct = Math.round(confidence * 100);
    const color = pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-2">
        <div className={`bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden ${size === 'lg' ? 'w-32 h-2.5' : 'w-16 h-2'}`}>
          <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`font-medium ${size === 'lg' ? 'text-sm text-gray-700 dark:text-gray-300' : 'text-xs text-gray-600 dark:text-gray-400'}`}>{pct}%</span>
      </div>
    );
  };

  return (
    <>
      {/* Sub-tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex gap-6">
          {subTabConfig.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setReviewTab(tab.key);
                setCurrentPage(1);
              }}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                reviewTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
              }`}
              title={tab.description}
            >
              {tab.label}
              {caseStats && (() => {
                const count = tab.key === 'ALL'
                  ? caseStats.total
                  : caseStats.byStatus[tab.key] ?? 0;
                return (
                  <span className={`ml-1.5 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    reviewTab === tab.key
                      ? 'bg-primary/10 text-primary'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    {count}
                  </span>
                );
              })()}
            </button>
          ))}
        </nav>
      </div>

      {/* Batch Actions */}
      {cases.length > 0 && reviewTab === 'FOR_VALIDATION' && (
        <div className="mb-4 flex items-center gap-3">
          <button
            onClick={async () => {
              const caseIds = cases.map((c) => c.id);
              const ok = await confirm({
                title: `Validate all ${caseIds.length} case${caseIds.length !== 1 ? 's' : ''}?`,
                message: 'This runs an AI validation on every case in this list. It uses tokens (billable), can take a while, and normally isn’t needed here — cases are validated automatically, and you can re-run validation on an individual case from its detail page. Proceed?',
                confirmLabel: 'Validate all',
                cancelLabel: 'Cancel',
              });
              if (ok) batchValidateMutation.mutate(caseIds);
            }}
            disabled={batchValidateMutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            <ArrowPathIcon
              className={`h-4 w-4 ${batchValidateMutation.isPending ? 'animate-spin' : ''}`}
            />
            {batchValidateMutation.isPending
              ? 'Validating...'
              : `Validate All (${cases.length})`}
          </button>
        </div>
      )}

      {/* Cases Table */}
      {casesLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow h-20" />
          ))}
        </div>
      ) : cases.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
          <ShieldCheckIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No cases in this queue</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            {reviewTab === 'ALL'
              ? 'No auto-sourced cases found'
              : reviewTab === 'FOR_VALIDATION'
              ? 'All scraped cases have been reviewed'
              : reviewTab === 'FOR_APPROVAL'
              ? 'No AI-validated cases awaiting approval'
              : 'No AI-rejected cases to review'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Case
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Country
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Confidence
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Coverage
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {cases.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-3">
                    <Link
                      to={`/cases/${c.id}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {c.caseNumber}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {c.country || <span className="text-gray-400 dark:text-gray-500">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.sourceInformation ? (
                      <div className="relative group">
                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate block max-w-[120px] cursor-default">
                          {c.sourceInformation.length > 30
                            ? c.sourceInformation.substring(0, 30) + '...'
                            : c.sourceInformation}
                        </span>
                        <div className="absolute z-20 bottom-full left-0 mb-1 hidden group-hover:block w-72 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg whitespace-pre-wrap break-words">
                          {c.sourceInformation}
                          <div className="absolute top-full left-4 w-2 h-2 bg-gray-900 rotate-45 -mt-1" />
                        </div>
                      </div>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                        {c.dataSource || 'scraper'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.validationStatus === 'VALIDATING' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                        <ArrowPathIcon className="h-3 w-3 animate-spin" />
                        Validating
                      </span>
                    ) : c.validationStatus === 'VALIDATED' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        <CheckCircleIcon className="h-3 w-3" />
                        {c.validationResults?.[0]?.confidence != null
                          ? `${(c.validationResults[0].confidence * 100).toFixed(0)}%`
                          : 'Validated'}
                      </span>
                    ) : c.validationResults?.[0]?.confidence != null
                      ? confidenceBar(c.validationResults[0].confidence)
                      : <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    {c.validationResults?.[0]?.completeness != null ? (() => {
                      const pct = Math.round((c.validationResults[0].completeness ?? 0) * 100);
                      const band = coverageBand(pct);
                      return (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${band.color}`}>
                          {band.label} · {pct}%
                        </span>
                      );
                    })() : <span className="text-xs text-gray-400 dark:text-gray-500">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                    {formatDistanceToNow(new Date(c.createdAt), { addSuffix: true })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/cases/${c.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-primary hover:border-primary"
                        title="Open the case to review and run validation"
                      >
                        <EyeIcon className="h-4 w-4" /> View case
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} cases)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/40 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
